import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Chip,
  Switch,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  PriorityHigh as PriorityHighIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { toDatetimeLocalValue, fromDatetimeLocalValue } from '../../utils/datetimeLocal';
import {
  ASSIGNABLE_USERS_QUERY_KEY,
  asAssignableUsers,
  fetchAssignableUsers,
} from '../../lib/assignableUsers';
import { getAuthToken } from '../../lib/authToken';

// Types
interface Task {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  taskType: string;
  dueDate: string | null;
  startDate: string | null;
  completedDate: string | null;
  estimatedHours: number | null;
  actualHours: number | null;
  isRecurring: boolean;
  recurrencePattern: string | null;
  recurrenceInterval: number | null;
  reminderEnabled: boolean;
  reminderTime: string | null;
  tags: string[] | string;
  notes: string | null;
  case: {
    id: string;
    caseNumber: string;
    title: string;
  } | null;
  client: {
    id: string;
    clientNumber: string;
    name: string;
  } | null;
  assignedUser: {
    id: string;
    name: string;
    email: string;
  } | null;
  creator: {
    id: string;
    name: string;
    email: string;
  } | null;
  createdAt: string | null;
  updatedAt: string | null;
  /** Write-only fields used when creating/updating (API accepts flat ids). */
  caseId?: string | null;
  clientId?: string | null;
  assignedTo?: string | null;
}

interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  taskData?: Task | null;
  mode: 'view' | 'edit' | 'create';
  onSave: (taskData: Partial<Task>) => Promise<void>;
}

const TaskDialog: React.FC<TaskDialogProps> = ({
  open,
  onClose,
  taskData,
  mode,
  onSave,
}) => {
  const [formData, setFormData] = useState<Partial<Task>>({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    taskType: 'other',
    dueDate: null,
    startDate: null,
    estimatedHours: null,
    actualHours: null,
    isRecurring: false,
    recurrencePattern: null,
    recurrenceInterval: null,
    reminderEnabled: true,
    reminderTime: null,
    tags: [],
    notes: '',
    caseId: null,
    clientId: null,
    assignedTo: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch users for assignment dropdown (staff can assign without users:read)
  const { data: usersData } = useQuery({
    queryKey: ASSIGNABLE_USERS_QUERY_KEY,
    queryFn: fetchAssignableUsers,
  });
  const users = asAssignableUsers(usersData);

  // Fetch cases for case dropdown
  const { data: casesData } = useQuery({
    queryKey: ['cases'],
    queryFn: async () => {
      const response = await fetch('/api/cases', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch cases');
      return response.json();
    },
  });

  // Fetch clients for client dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const response = await fetch('/api/clients', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch clients');
      return response.json();
    },
  });

  useEffect(() => {
    if (taskData && mode !== 'create') {
      // Parse tags from JSON string to array
      let tags = [];
      if (taskData.tags) {
        if (typeof taskData.tags === 'string') {
          try {
            tags = JSON.parse(taskData.tags);
          } catch (error) {
            console.error('Error parsing tags:', error);
            tags = [];
          }
        } else if (Array.isArray(taskData.tags)) {
          tags = taskData.tags;
        }
      }

      setFormData({
        title: taskData.title,
        description: taskData.description,
        status: taskData.status,
        priority: taskData.priority,
        taskType: taskData.taskType,
        dueDate: taskData.dueDate,
        startDate: taskData.startDate,
        estimatedHours: taskData.estimatedHours,
        actualHours: taskData.actualHours,
        isRecurring: taskData.isRecurring,
        recurrencePattern: taskData.recurrencePattern,
        recurrenceInterval: taskData.recurrenceInterval,
        reminderEnabled: taskData.reminderEnabled,
        reminderTime: taskData.reminderTime,
        tags: tags,
        notes: taskData.notes,
        caseId: taskData.case?.id || null,
        clientId: taskData.client?.id || null,
        assignedTo: taskData.assignedUser?.id || null,
      });
    } else {
      setFormData({
        title: '',
        description: '',
        status: 'pending',
        priority: 'medium',
        taskType: 'other',
        dueDate: null,
        startDate: null,
        estimatedHours: null,
        actualHours: null,
        isRecurring: false,
        recurrencePattern: null,
        recurrenceInterval: null,
        reminderEnabled: true,
        reminderTime: null,
        tags: [],
        notes: '',
        caseId: null,
        clientId: null,
        assignedTo: null,
      });
    }
  }, [taskData, mode]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error('Please enter a task title');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving task:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'N/A';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'in_progress':
        return <AssignmentIcon color="primary" />;
      case 'cancelled':
        return <CancelIcon color="error" />;
      case 'on_hold':
        return <PauseIcon color="warning" />;
      default:
        return <ScheduleIcon color="action" />;
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return <PriorityHighIcon color="error" />;
      case 'high':
        return <PriorityHighIcon color="warning" />;
      default:
        return <PriorityHighIcon color="action" />;
    }
  };

  const getTaskTypeIcon = (taskType: string) => {
    switch (taskType) {
      case 'client_meeting':
        return <PersonIcon />;
      case 'court_hearing':
        return <BusinessIcon />;
      case 'document_review':
        return <DescriptionIcon />;
      default:
        return <AssignmentIcon />;
    }
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'on_hold', label: 'On Hold' },
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ];

  const taskTypeOptions = [
    { value: 'client_meeting', label: 'Client Meeting' },
    { value: 'court_hearing', label: 'Court Hearing' },
    { value: 'document_review', label: 'Document Review' },
    { value: 'research', label: 'Research' },
    { value: 'filing_deadline', label: 'Filing Deadline' },
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'billing', label: 'Billing' },
    { value: 'administrative', label: 'Administrative' },
    { value: 'case_preparation', label: 'Case Preparation' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'settlement_negotiation', label: 'Settlement Negotiation' },
    { value: 'trial_preparation', label: 'Trial Preparation' },
    { value: 'other', label: 'Other' },
  ];

  const recurrenceOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  const cases = casesData?.data?.cases || [];
  const clients = clientsData?.data?.clients || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {mode === 'create' && <AssignmentIcon />}
          {mode === 'edit' && <AssignmentIcon color="primary" />}
          {mode === 'view' && <AssignmentIcon color="action" />}
          <Typography variant="h6">
            {mode === 'create' ? 'Create New Task' : 
             mode === 'edit' ? 'Edit Task' : 'Task Details'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {mode === 'view' ? (
          // View Mode
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {taskData?.title}
                </Typography>
                {taskData?.description && (
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {taskData.description}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {getStatusIcon(taskData?.status || 'pending')}
                  <Typography variant="subtitle2">Status</Typography>
                </Box>
                <Chip
                  label={taskData?.status?.replace('_', ' ') || 'Pending'}
                  color={taskData?.status === 'completed' ? 'success' : 'default'}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {getPriorityIcon(taskData?.priority || 'medium')}
                  <Typography variant="subtitle2">Priority</Typography>
                </Box>
                <Chip
                  label={taskData?.priority || 'Medium'}
                  color={taskData?.priority === 'urgent' ? 'error' : 'default'}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {getTaskTypeIcon(taskData?.taskType || 'other')}
                  <Typography variant="subtitle2">Type</Typography>
                </Box>
                <Typography variant="body2">
                  {taskData?.taskType?.replace('_', ' ') || 'Other'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Due Date
                </Typography>
                <Typography variant="body2">
                  {formatDate(taskData?.dueDate)}
                </Typography>
              </Grid>

              {taskData?.assignedUser && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Assigned To
                  </Typography>
                  <Typography variant="body2">
                    {taskData.assignedUser.name}
                  </Typography>
                </Grid>
              )}

              {taskData?.case && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Case
                  </Typography>
                  <Typography variant="body2">
                    {taskData.case.caseNumber} - {taskData.case.title}
                  </Typography>
                </Grid>
              )}

              {taskData?.client && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Client
                  </Typography>
                  <Typography variant="body2">
                    {taskData.client.name}
                  </Typography>
                </Grid>
              )}

              {taskData?.estimatedHours && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Estimated Hours
                  </Typography>
                  <Typography variant="body2">
                    {taskData.estimatedHours} hours
                  </Typography>
                </Grid>
              )}

              {taskData?.actualHours && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Actual Hours
                  </Typography>
                  <Typography variant="body2">
                    {taskData.actualHours} hours
                  </Typography>
                </Grid>
              )}

              {taskData?.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body2">
                    {taskData.notes}
                  </Typography>
                </Grid>
              )}

              {taskData?.tags && Array.isArray(taskData.tags) && taskData.tags.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tags
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {taskData.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider />
                <Box mt={2}>
                  <Typography variant="caption" color="textSecondary">
                    Created: {formatDate(taskData?.createdAt)}
                  </Typography>
                  {taskData?.updatedAt && taskData.updatedAt !== taskData.createdAt && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Updated: {formatDate(taskData.updatedAt)}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          // Edit/Create Mode
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title *"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    label="Status"
                    disabled={mode === 'view'}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    label="Priority"
                    disabled={mode === 'view'}
                  >
                    {priorityOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Task Type</InputLabel>
                  <Select
                    value={formData.taskType}
                    onChange={(e) => handleInputChange('taskType', e.target.value)}
                    label="Task Type"
                    disabled={mode === 'view'}
                  >
                    {taskTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="datetime-local"
                  value={toDatetimeLocalValue(formData.dueDate)}
                  onChange={(e) => handleInputChange('dueDate', fromDatetimeLocalValue(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="datetime-local"
                  value={toDatetimeLocalValue(formData.startDate)}
                  onChange={(e) => handleInputChange('startDate', fromDatetimeLocalValue(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={formData.assignedTo || ''}
                    onChange={(e) => handleInputChange('assignedTo', e.target.value || null)}
                    label="Assigned To"
                    disabled={mode === 'view'}
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {users.map((user: any) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Case</InputLabel>
                  <Select
                    value={formData.caseId || ''}
                    onChange={(e) => handleInputChange('caseId', e.target.value || null)}
                    label="Case"
                    disabled={mode === 'view'}
                  >
                    <MenuItem value="">
                      <em>No Case</em>
                    </MenuItem>
                    {cases.map((caseItem: any) => (
                      <MenuItem key={caseItem.id} value={caseItem.id}>
                        {caseItem.caseNumber} - {caseItem.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={formData.clientId || ''}
                    onChange={(e) => handleInputChange('clientId', e.target.value || null)}
                    label="Client"
                    disabled={mode === 'view'}
                  >
                    <MenuItem value="">
                      <em>No Client</em>
                    </MenuItem>
                    {clients.map((client: any) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.firstName} {client.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Estimated Hours"
                  type="number"
                  value={formData.estimatedHours || ''}
                  onChange={(e) => handleInputChange('estimatedHours', e.target.value ? parseFloat(e.target.value) : null)}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Actual Hours"
                  type="number"
                  value={formData.actualHours || ''}
                  onChange={(e) => handleInputChange('actualHours', e.target.value ? parseFloat(e.target.value) : null)}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={(() => {
                    const tags = formData.tags;
                    if (Array.isArray(tags)) {
                      return tags;
                    }
                    if (typeof tags === 'string') {
                      try {
                        const parsed = JSON.parse(tags);
                        return Array.isArray(parsed) ? parsed : [];
                      } catch (error) {
                        return [];
                      }
                    }
                    return [];
                  })()}
                  onChange={(event, newValue) => handleInputChange('tags', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      placeholder="Add tags..."
                    />
                  )}
                  renderTags={(value, getTagProps) => {
                    const safeValue = Array.isArray(value) ? value : [];
                    return safeValue.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                      />
                    ));
                  }}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  multiline
                  rows={3}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isRecurring || false}
                      onChange={(e) => handleInputChange('isRecurring', e.target.checked)}
                      disabled={mode === 'view'}
                    />
                  }
                  label="Recurring Task"
                />
              </Grid>

              {formData.isRecurring && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Recurrence Pattern</InputLabel>
                      <Select
                        value={formData.recurrencePattern || ''}
                        onChange={(e) => handleInputChange('recurrencePattern', e.target.value)}
                        label="Recurrence Pattern"
                        disabled={mode === 'view'}
                      >
                        {recurrenceOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Recurrence Interval"
                      type="number"
                      value={formData.recurrenceInterval || ''}
                      onChange={(e) => handleInputChange('recurrenceInterval', e.target.value ? parseInt(e.target.value) : null)}
                      disabled={mode === 'view'}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.reminderEnabled || false}
                      onChange={(e) => handleInputChange('reminderEnabled', e.target.checked)}
                      disabled={mode === 'view'}
                    />
                  }
                  label="Enable Reminder"
                />
              </Grid>

              {formData.reminderEnabled && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Reminder Time"
                    type="datetime-local"
                    value={toDatetimeLocalValue(formData.reminderTime)}
                    onChange={(e) => handleInputChange('reminderTime', fromDatetimeLocalValue(e.target.value))}
                    InputLabelProps={{ shrink: true }}
                    disabled={mode === 'view'}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {mode === 'view' ? 'Close' : 'Cancel'}
        </Button>
        {(mode === 'edit' || mode === 'create') && (
          <Button
            variant="contained"
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Task'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TaskDialog; 